import { Link } from 'react-router-dom';
import { Filter, Calendar } from 'lucide-react';
import { useState, useMemo } from "react";
import { useProductImage } from '@/hooks/use-product-image';

interface OilRecommendation {
  oilType: string;
  viscosity: string;
  description: string;
  price: string;
  image: string;
  benefits: string[];
  volume: string;
}
interface OilRecommendationsSectionProps {
  recommendations: OilRecommendation[];
  vehicleData: any;
  formSubmitted: boolean;
  litersNeeded?: number;
}

const OilRecommendationsSection = ({
  recommendations,
  vehicleData,
  formSubmitted,
  litersNeeded = 4,
}: OilRecommendationsSectionProps) => {
  // ---- All hooks must be here at the top, before any return ----
  const { getSafeImageUrl, getAllPossibleImageUrls, fallbackImage } = useProductImage();
  const [imageRetries, setImageRetries] = useState<{ [key: number]: number }>({});
  const possibleImages = useMemo(() => (
    recommendations.map(rec => getAllPossibleImageUrls(rec.oilType))
  ), [recommendations, getAllPossibleImageUrls]);

  // ---- END OF HOOKS ----

  if (!formSubmitted) return null;

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("en-IL", {
      style: "currency",
      currency: "ILS",
      maximumFractionDigits: 2,
    }).format(value);
  };

  const getPriceNumber = (str: string) => {
    const num = parseFloat(str.replace(/[^0-9.]/g, ""));
    return isNaN(num) ? 0 : num;
  };

  const getDisplayedImage = (oil: OilRecommendation, idx: number) => {
    const retryIndex = imageRetries[idx] || 0;
    const candidateImages = possibleImages[idx];
    if (candidateImages && candidateImages.length > retryIndex) {
      return candidateImages[retryIndex];
    }
    return getSafeImageUrl(oil.image) || fallbackImage;
  };

  const handleImageError = (idx: number) => (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
    setImageRetries(prev => {
      const current = prev[idx] || 0;
      return { ...prev, [idx]: current + 1 };
    });
    const target = e.target as HTMLImageElement;
    target.src = fallbackImage;
    console.log('Image failed to load, trying alternate or fallback image');
  };

  return <section className="section-padding bg-white animate-fade-in">
      <div className="page-container">
        <div className="mb-10">
          <span className="inline-block px-3 py-1 text-xs font-semibold text-autocare-blue bg-autocare-blue/10 rounded-full mb-4">
            Oil Recommendations
          </span>
          <h2 className="text-3xl font-bold text-autocare-blue mb-4">Recommended Oil Types</h2>
          <p className="text-gray-600 max-w-2xl">
            Based on your {vehicleData?.year} {vehicleData?.make} {vehicleData?.model} with {vehicleData?.engineType} engine, here are our top recommendations. Choose the option that best fits your needs and budget.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {recommendations.map((oil, index) => {
            const pricePer4L = getPriceNumber(oil.price);
            const liters = litersNeeded;
            const imageSrc = getDisplayedImage(oil, index);
            return (
              <div key={index} className="bg-white rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all border border-autocare-gray-medium/30">
                <div className="relative h-48">
                  <img 
                    src={imageSrc} 
                    alt={`${oil.oilType} Oil`} 
                    className="w-full h-full object-contain p-4"
                    onError={handleImageError(index)} 
                  />
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent px-4 py-3">
                    <div className="text-sm font-medium text-white">Recommended Viscosity</div>
                    <div className="text-xl font-bold text-white">{oil.viscosity}</div>
                  </div>
                </div>
                <div className="p-5">
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="text-xl font-semibold text-autocare-blue">{oil.oilType}</h3>
                    <div className="text-autocare-yellow font-bold">
                      {formatCurrency(pricePer4L)} / 4L
                    </div>
                  </div>
                  <p className="text-gray-600 mb-2 text-sm">{oil.description}</p>
                  <div className="mb-4 flex flex-col gap-2">
                    <div className="flex items-center text-base">
                      <span className="font-semibold text-autocare-blue mr-1">Required (Liters):</span>
                      <span className="font-bold mr-2">{liters}</span>
                      <span className="text-xs text-gray-500">(Set above)</span>
                    </div>
                    <div className="flex items-center text-base">
                      <span className="font-semibold text-autocare-blue mr-1">Total price:</span>
                      <span className="font-bold text-autocare-yellow">
                        {formatCurrency((pricePer4L * liters) / 4)}
                      </span>
                    </div>
                  </div>
                  <div className="mb-4">
                    <h4 className="font-medium mb-2 text-sm">Benefits:</h4>
                    <ul className="space-y-1">
                      {oil.benefits.map((benefit, i) => <li key={i} className="flex items-start text-sm">
                        <span className="text-autocare-blue mt-1 mr-2 flex-shrink-0">•</span>
                        <span className="text-gray-600">{benefit}</span>
                      </li>)}
                    </ul>
                  </div>
                  <div className="flex flex-col space-y-2">
                    <Link to="/booking" className="w-full py-2 rounded-lg bg-autocare-yellow text-autocare-blue font-medium hover:bg-autocare-yellow-light transition-colors flex justify-center items-center">
                      <Calendar className="h-4 w-4 mr-2" />
                      Book Oil Change
                    </Link>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
        
        <div className="mt-12 text-center">
          <p className="text-gray-600 mb-6">
            Not sure which option to choose? Our mechanics can help you make the right decision.
          </p>
          <Link to="/shops" className="btn-primary">
            Find a Service Shop Near You
          </Link>
        </div>
      </div>
    </section>;
};
export default OilRecommendationsSection;
